import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { History, Route, Clock, MapPin, Truck, Car, Sparkles } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

interface RouteHistoryRecord {
  id: string
  route_id: string | null
  route_name: string | null
  vehicle_name: string | null
  driver_name: string | null
  calculation_type: string
  waypoint_count: number
  total_distance: number
  total_duration: number
  costing_type: string
  status: string
  created_at: number
}

export function RouteHistory() {
  const { data, isLoading } = useQuery({
    queryKey: ['route-history'],
    queryFn: async () => {
      const res = await fetch('/api/routing/history')
      if (!res.ok) throw new Error('Failed to fetch route history')
      return res.json() as Promise<{ history: RouteHistoryRecord[] }>
    },
    refetchInterval: 60000,
  })

  const formatDistance = (meters: number) => {
    const miles = meters / 1609.34
    return miles.toFixed(1) + ' mi'
  }

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`
  }

  const getStatusVariant = (status: string): 'default' | 'secondary' | 'outline' | 'destructive' => {
    const variants: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
      completed: 'default',
      started: 'secondary',
      calculated: 'outline',
      cancelled: 'destructive'
    }
    return variants[status] || 'outline'
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-5 w-5" />
          Route Calculation History
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            Loading route history...
          </div>
        ) : !data?.history || data.history.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <History className="h-12 w-12 mx-auto mb-3 opacity-20" />
            <p>No route history yet</p>
            <p className="text-sm mt-1">Route calculations will appear here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {data.history.map((record, index) => {
              const CostingIcon = record.costing_type === 'truck' ? Truck : Car
              const isOptimized = record.calculation_type === 'optimized'

              return (
                <div key={record.id} className="relative">
                  {/* Timeline line */}
                  {index !== data.history.length - 1 && (
                    <div className="absolute left-[15px] top-[40px] bottom-[-12px] w-0.5 bg-border" />
                  )}

                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        {/* Icon */}
                        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-primary/10">
                          <Route className="h-4 w-4 text-primary" />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0 space-y-2">
                          {/* Header */}
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <h3 className="font-semibold">
                                  {record.route_name || 'Ad-hoc Route'}
                                </h3>
                                <Badge variant={getStatusVariant(record.status)}>
                                  {record.status}
                                </Badge>
                                {isOptimized && (
                                  <Badge variant="outline" className="gap-1">
                                    <Sparkles className="h-3 w-3" />
                                    Optimized
                                  </Badge>
                                )}
                              </div>
                              {(record.vehicle_name || record.driver_name) && (
                                <p className="text-sm text-muted-foreground">
                                  {record.vehicle_name}
                                  {record.vehicle_name && record.driver_name && ' • '}
                                  {record.driver_name}
                                </p>
                              )}
                            </div>

                            {/* Date */}
                            <div className="text-right text-sm text-muted-foreground">
                              {formatDistanceToNow(new Date(record.created_at * 1000), {
                                addSuffix: true
                              })}
                            </div>
                          </div>

                          {/* Stats */}
                          <div className="flex flex-wrap gap-4 text-sm">
                            <div className="flex items-center gap-1">
                              <MapPin className="h-4 w-4 text-muted-foreground" />
                              <span>{record.waypoint_count} waypoints</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Route className="h-4 w-4 text-muted-foreground" />
                              <span>{formatDistance(record.total_distance)}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="h-4 w-4 text-muted-foreground" />
                              <span>{formatDuration(record.total_duration)}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <CostingIcon className="h-4 w-4 text-muted-foreground" />
                              <span className="capitalize">{record.costing_type}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
